const AUTOMATIC_LATEST_SELECTOR = '[data-automatic-latest-products][data-latest-products-url]';
const initializedSections = new WeakSet();

/**
 * Re-renders an automatic latest-products section in a collection request.
 * Shopify applies `created-descending` before pagination in that context, so
 * the result remains correct when the catalog grows beyond Liquid's 50-product
 * per-page limit.
 * @param {HTMLElement} section
 */
async function loadLatestProducts(section) {
  if (initializedSections.has(section)) return;

  const sectionId = section.dataset.sectionId;
  const latestProductsUrl = section.dataset.latestProductsUrl;

  if (!sectionId || !latestProductsUrl) return;

  initializedSections.add(section);
  section.setAttribute('aria-busy', 'true');

  try {
    const url = new URL(latestProductsUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) throw new Error(`Latest products request failed with status ${response.status}`);

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const replacement = documentFragment.querySelector(
      `[data-automatic-latest-products][data-section-id="${CSS.escape(sectionId)}"]`
    );

    if (!(replacement instanceof HTMLElement)) throw new Error('Latest products section was not returned');
    if (!section.isConnected) return;

    section.replaceWith(replacement);
  } catch (error) {
    section.removeAttribute('aria-busy');
    section.dataset.latestProductsError = 'true';
    console.warn('[automatic-latest-products]', error);
  }
}

/** @param {ParentNode} root */
function initializeAutomaticLatestProducts(root = document) {
  const sections = root instanceof Element && root.matches(AUTOMATIC_LATEST_SELECTOR) ? [root] : [];
  sections.push(...root.querySelectorAll(AUTOMATIC_LATEST_SELECTOR));

  for (const section of sections) {
    if (section instanceof HTMLElement) loadLatestProducts(section);
  }
}

initializeAutomaticLatestProducts();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) initializeAutomaticLatestProducts(event.target);
});
